import { Minus, Plus } from "lucide-react";
import { euro } from "@/lib/api";
import { SUB_INTERVALS, every } from "@/lib/subscription";

const MAX_LINES = 8;
const MAX_QTY = 5;

/** Réassort -10 % : tout le catalogue avec quantités. `value` = [{ slug, quantity }]. */
export function ReassortPicker({ products, value, onChange }) {
  const list = Array.isArray(products) ? products : [];
  const qty = (slug) => value.find((i) => i.slug === slug)?.quantity || 0;
  const set = (slug, n) => {
    const rest = value.filter((i) => i.slug !== slug);
    if (n <= 0) return onChange(rest);
    if (!qty(slug) && value.length >= MAX_LINES) return undefined;
    return onChange(qty(slug) ? value.map((i) => (i.slug === slug ? { ...i, quantity: n } : i)) : [...value, { slug, quantity: n }]);
  };
  return (
    <ul data-testid="reassort-picker" className="grid gap-3 sm:grid-cols-2">
      {list.map((p) => {
        const n = qty(p.slug);
        return (
          <li key={p.slug} data-testid={`reassort-item-${p.slug}`} className={`flex items-center gap-3 rounded-sm border p-3 transition-colors ${n ? "border-[var(--gold)] bg-[rgba(217,180,106,0.08)]" : "border-[var(--line)]"}`}>
            <img src={p.image} alt={p.name} loading="lazy" className="h-16 w-16 shrink-0 rounded-sm object-cover" />
            <div className="min-w-0 flex-1">
              <p className="text-sm leading-snug">{p.name}{p.type === "pack" ? <span className="ml-2 text-[11px] uppercase tracking-[0.16em] text-[var(--gold-soft)]">Pack</span> : null}</p>
              <p className="mt-1 text-xs text-[var(--parchment)]/80"><s>{euro(p.price)}</s> <b className="text-[var(--gold-soft)]">{euro(p.sub_price)}</b></p>
            </div>
            {n ? (
              <div className="flex items-center gap-2">
                <button type="button" data-testid={`reassort-minus-${p.slug}`} onClick={() => set(p.slug, n - 1)} aria-label="Retirer" className="flex h-8 w-8 items-center justify-center rounded-full border border-[var(--line)] hover:border-[var(--gold)]"><Minus className="h-3 w-3" /></button>
                <span data-testid={`reassort-qty-${p.slug}`} className="w-4 text-center text-sm">{n}</span>
                <button type="button" data-testid={`reassort-plus-${p.slug}`} onClick={() => set(p.slug, Math.min(MAX_QTY, n + 1))} aria-label="Ajouter" className="flex h-8 w-8 items-center justify-center rounded-full border border-[var(--line)] hover:border-[var(--gold)]"><Plus className="h-3 w-3" /></button>
              </div>
            ) : (
              <button type="button" data-testid={`reassort-add-${p.slug}`} onClick={() => set(p.slug, 1)} disabled={value.length >= MAX_LINES}
                className="rounded-full border border-[var(--gold)]/70 px-4 py-2 text-xs uppercase tracking-[0.16em] text-[var(--gold-soft)] transition-colors hover:bg-[var(--gold)] hover:text-[var(--ink)] disabled:opacity-40">Ajouter</button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function IntervalChooser({ value, onChange }) {
  return (
    <div data-testid="reassort-intervals" className="flex flex-wrap gap-3">
      {SUB_INTERVALS.map((n) => (
        <button key={n} type="button" data-testid={`reassort-interval-${n}`} onClick={() => onChange(n)}
          className={`rounded-full border px-5 py-3 text-sm transition-colors ${value === n ? "border-[var(--gold)] bg-[var(--gold)] text-[var(--ink)]" : "border-[var(--line)] hover:border-[var(--gold)]"}`}>
          Livraison {every(n)}
        </button>
      ))}
    </div>
  );
}

/** Total produits (prix abonnés) et total normal d'une sélection. */
export function reassortTotals(products, value) {
  const by = Object.fromEntries((Array.isArray(products) ? products : []).map((p) => [p.slug, p]));
  const sum = (k) => Math.round(value.reduce((s, i) => s + (by[i.slug]?.[k] || 0) * i.quantity, 0) * 100) / 100;
  return { total: sum("sub_price"), normal: sum("price"), by };
}
